import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Sparkles, PenTool, TrendingUp, Bookmark, Clock, ArrowRight, Lightbulb, Hash, FileText, AlertTriangle, CreditCard, ShieldAlert
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Account status checks
  const isTrialActive = user?.accountStatus === 'trial_active';
  const isTrialExpired = user?.accountStatus === 'trial_expired';
  const isCancelled = user?.accountStatus === 'cancelled';
  const isGracePeriod = user?.accountStatus === 'grace_period';
  const isBlocked = isTrialExpired || isCancelled;
  
  // Determine welcome greeting - use isFirstLogin from login response if available
  // localStorage stores the flag from the most recent login
  const isFirstLogin = localStorage.getItem('isFirstLogin') === 'true';
  const welcomeGreeting = isFirstLogin ? 'Welcome' : 'Welcome back';

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (e) {
      console.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getPlanName = (plan, accountStatus) => {
    if (accountStatus === 'trial_active') return 'Free Trial';
    if (accountStatus === 'trial_expired') return 'Trial Expired';
    if (accountStatus === 'cancelled') return 'Cancelled';
    if (accountStatus === 'grace_period') return 'Payment Issue';
    if (plan === 'max') return 'Creator Max';
    if (plan === 'pro') return 'Pro Creator';
    if (plan === 'beginner') return 'Beginner';
    return 'No Plan';
  };

  // Calculate trial days remaining using floor
  // Add 1 to include the current partial day (e.g., 71.5 hours = 2.98 days → floor(2.98)+1 = 3 days)
  const getTrialDaysRemaining = () => {
    if (!user?.trialEndTimestamp) return 0;
    const endDate = new Date(user.trialEndTimestamp);
    const now = new Date();
    const diffMs = endDate - now;
    if (diffMs <= 0) return 0;
    // Convert to days and use floor, then add 1 for current partial day
    const fullDaysRemaining = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    // If there's any time remaining beyond full days, count that as +1 day
    const hasPartialDay = (diffMs % (1000 * 60 * 60 * 24)) > 0;
    return fullDaysRemaining + (hasPartialDay ? 1 : 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Calculate credits based on account status
  const totalCredits = isTrialActive ? (stats?.trialCredits || user?.trialCredits || 0) : (stats?.totalCredits || 0);
  const savedCount = stats?.savedContentCount || 0;

  // Credit progress calculation
  const PLAN_CREDIT_CAPS = { beginner: 50, pro: 200, max: 600 };
  const TRIAL_CREDIT_CAP = 20;
  const maxCredits = isTrialActive ? TRIAL_CREDIT_CAP : (PLAN_CREDIT_CAPS[user?.subscriptionPlan] || totalCredits || 1);
  const creditsUsed = Math.max(0, maxCredits - totalCredits);
  const usagePercent = maxCredits > 0 ? Math.round((creditsUsed / maxCredits) * 100) : 0;
  const progressColor = usagePercent >= 80 ? 'bg-red-500' : usagePercent >= 50 ? 'bg-amber-500' : 'bg-emerald-500';
  const progressTrackColor = usagePercent >= 80 ? 'bg-red-500/15' : usagePercent >= 50 ? 'bg-amber-500/15' : 'bg-emerald-500/15';
  const progressTextColor = usagePercent >= 80 ? 'text-red-400' : usagePercent >= 50 ? 'text-amber-400' : 'text-emerald-400';
  const usageMessage = usagePercent < 50 ? 'Keep building momentum' : usagePercent < 80 ? "You're halfway there. Don't slow down." : "You're almost out of credits. Upgrade to keep creating.";
  const showHeaderUpgrade = isBlocked || (totalCredits > 0 && maxCredits > 0 && (totalCredits / maxCredits) <= 0.2);

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Header */}
      <div>
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          {welcomeGreeting}, {user?.name}!
        </h1>
        <p className="text-slate-400 text-lg">
          Ready to create your next viral hit? Choose a tool below to get started.
        </p>
      </div>

      {/* Trial Expired Banner */}
      {isTrialExpired && (
        <Card className="bg-red-500/10 border-red-500/30" data-testid="banner-trial-expired">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-red-200 font-medium">Your free trial has ended</p>
                <p className="text-red-200/70 text-sm">Upgrade to continue creating content.</p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => navigate('/billing')}
              data-testid="banner-upgrade-btn"
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancelled Subscription Banner */}
      {isCancelled && (
        <Card className="bg-amber-500/10 border-amber-500/30" data-testid="banner-cancelled">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-amber-200 font-medium">Your subscription has ended</p>
                <p className="text-amber-200/70 text-sm">Renew your plan to continue using CreatorFlow.</p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => navigate('/billing')}
              data-testid="banner-renew-btn"
            >
              Renew Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Grace Period Banner */}
      {isGracePeriod && (
        <Card className="bg-orange-500/10 border-orange-500/30" data-testid="banner-grace-period">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-orange-400" />
              <div>
                <p className="text-orange-200 font-medium">Payment issue detected</p>
                <p className="text-orange-200/70 text-sm">Please update your payment method to avoid losing access.</p>
              </div>
            </div>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => navigate('/billing')}
              data-testid="banner-update-payment-btn"
            >
              Update Payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trial Active Banner */}
      {isTrialActive && (
        <Card className="bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border-violet-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-violet-400" />
              <div>
                <p className="text-white font-medium">Free Trial Active</p>
                <p className="text-slate-300 text-sm">
                  {getTrialDaysRemaining()} day{getTrialDaysRemaining() !== 1 ? 's' : ''} remaining • {totalCredits} credits left • Access to basic tools
                </p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => navigate('/billing')}
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Next Step Banner - only show for active users */}
      {!isBlocked && (
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <span className="text-white font-medium">Next step:</span>
            <span className="text-violet-400">Generate a fresh batch of content ideas.</span>
          </div>
        </div>
      )}

      {/* Credits Alert */}
      {!isBlocked && (
        <Card className="bg-slate-800/30 border-slate-700/50" data-testid="credit-progress-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-300 font-medium text-sm">{isTrialActive ? 'Trial credits' : 'Monthly credits'}</span>
              <span className={`text-sm font-semibold ${progressTextColor}`} data-testid="credit-usage-text">
                {creditsUsed} / {maxCredits} used ({usagePercent}%)
              </span>
            </div>
            <div className={`w-full h-2.5 rounded-full ${progressTrackColor}`} data-testid="credit-progress-track">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${progressColor}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
                data-testid="credit-progress-bar"
              />
            </div>
            <p className={`mt-2.5 text-sm ${progressTextColor}`} data-testid="credit-usage-message">
              {usageMessage}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Credits Remaining */}
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-violet-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">{isTrialActive ? 'Trial Credits' : 'Credits Remaining'}</p>
                <p className="text-3xl font-bold text-white" data-testid="total-credits">{totalCredits}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Saved Items */}
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center">
                <Bookmark className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Saved Items</p>
                <p className="text-3xl font-bold text-white" data-testid="saved-count">{savedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan */}
        <Card className={`border-slate-700/50 ${
          isTrialActive ? 'bg-amber-500/10' : isBlocked ? 'bg-red-500/10' : 'bg-slate-800/30'
        }`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isTrialActive ? 'bg-amber-500/20' : isBlocked ? 'bg-red-500/20' : 'bg-orange-500/10'
              }`}>
                {isBlocked ? (
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                ) : (
                  <Clock className={`w-6 h-6 ${isTrialActive ? 'text-amber-400' : 'text-orange-400'}`} />
                )}
              </div>
              <div>
                <p className="text-slate-400 text-sm">Plan</p>
                <p className={`text-2xl font-bold ${
                  isTrialActive ? 'text-amber-400' : isBlocked ? 'text-red-400' : 'text-white'
                }`}>
                  {getPlanName(user?.subscriptionPlan, user?.accountStatus)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Start Tools — with soft paywall overlay for blocked users */}
      <div className="relative">
        {isBlocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center" data-testid="soft-paywall-overlay">
            <div className="absolute inset-0 bg-[#0f1629]/60 backdrop-blur-[2px] rounded-2xl" />
            <Card className="relative z-20 bg-slate-900/95 border-violet-500/40 max-w-md w-full mx-4 shadow-2xl shadow-violet-500/10" data-testid="upgrade-card">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-violet-500/15 flex items-center justify-center mx-auto mb-5">
                  {isCancelled ? (
                    <CreditCard className="w-7 h-7 text-violet-400" />
                  ) : (
                    <Sparkles className="w-7 h-7 text-violet-400" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {isCancelled ? 'Subscription Ended' : 'Trial Ended'}
                </h3>
                <p className="text-slate-400 text-sm mb-6">
                  {isCancelled 
                    ? 'Renew your plan to unlock all tools and keep creating.'
                    : 'Upgrade to a plan to unlock all tools and keep creating.'}
                </p>
                <Button 
                  className="w-full bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 text-white font-semibold py-3"
                  onClick={() => navigate('/billing')}
                  data-testid="overlay-upgrade-btn"
                >
                  {isCancelled ? 'Renew Subscription' : 'View Plans'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        <div className={isBlocked ? 'pointer-events-none' : ''}>
        <h2 className="text-xl font-bold text-white mb-4">Quick Start Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Generate Ideas */}
          <Card className={`border-slate-700/50 hover:border-slate-600/50 transition-colors ${
            isBlocked ? 'bg-slate-900/30 opacity-50' : 'bg-slate-900/50'
          }`}>
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center mb-6">
                <Lightbulb className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Generate Ideas</h3>
              <p className="text-slate-400 text-sm mb-6">
                Get unlimited content ideas for your niche instantly.
              </p>
              <Button 
                onClick={() => isBlocked ? navigate('/billing') : navigate('/ideas')}
                className={`w-full ${isBlocked ? 'bg-slate-600' : 'bg-violet-500 hover:bg-violet-600'} text-white`}
                disabled={isBlocked}
                data-testid="quick-action-ideas"
              >
                Start Creating <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Writing Tools */}
          <Card className={`border-slate-700/50 hover:border-slate-600/50 transition-colors ${
            isBlocked ? 'bg-slate-900/30 opacity-50' : 'bg-slate-900/50'
          }`}>
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                <Hash className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Writing Tools</h3>
              <p className="text-slate-400 text-sm mb-6">
                Create hooks, captions, and hashtags.
              </p>
              <Button 
                onClick={() => isBlocked ? navigate('/billing') : navigate('/writing')}
                className={`w-full ${isBlocked ? 'bg-slate-600' : 'bg-violet-500 hover:bg-violet-600'} text-white`}
                disabled={isBlocked}
                data-testid="quick-action-writing"
              >
                Start Creating <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Write Scripts */}
          <Card className={`border-slate-700/50 hover:border-slate-600/50 transition-colors ${
            isBlocked ? 'bg-slate-900/30 opacity-50' : 'bg-slate-900/50'
          }`}>
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-6">
                <FileText className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Write Scripts</h3>
              <p className="text-slate-400 text-sm mb-6">
                Full video scripts generated in seconds.
              </p>
              <Button 
                onClick={() => isBlocked ? navigate('/billing') : navigate('/scripts')}
                className={`w-full ${isBlocked ? 'bg-slate-600' : 'bg-violet-500 hover:bg-violet-600'} text-white`}
                disabled={isBlocked}
                data-testid="quick-action-scripts"
              >
                Start Creating <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
